import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import TopBar from '../components/TopBar';
import ChannelsPanel from '../components/settings/ChannelsPanel';
import AgentTestConsole from '../components/settings/AgentTestConsole';
import VoiceProvidersPanel from '../components/settings/VoiceProvidersPanel';
import BestSellersPanel from '../components/settings/BestSellersPanel';
import LeadFollowUpPanel from '../components/settings/LeadFollowUpPanel';
import LocationsPanel from '../components/settings/LocationsPanel';
import TestDriveDocumentsPanel from '../components/settings/TestDriveDocumentsPanel';
import { ChannelMonitorFeed } from './ChannelMonitor';
import { useTheme, THEMES } from '../lib/theme';
import { usersService, channelsService, voiceProvidersService, organizationService } from '../lib/services';
import { setOrgTimezone, orgTimezoneAbbrev } from '../lib/datetime';
import { ROLE_META, ASSIGNABLE_ROLES, roleLabel, roleTone } from '../lib/roles';

/**
 * Settings tabs, in nav order. Each entry owns its own label + panel so the
 * left rail and the content area can never drift out of sync (they used to be
 * a string list + a hardcoded `activeNav === N` switch, which broke every time
 * a tab was inserted or removed).
 *
 * Panels are function declarations below, so they're hoisted — referencing
 * them here is safe even though they're defined later in the file.
 */
const SETTINGS_TABS = [
  { label: 'Appearance', render: () => <AppearancePanel /> },
  { label: 'Organization', render: () => <OrganizationPanel /> },
  // Multi-rooftop dealerships name their sites here (T079 / E10) — the
  // dashboard's location filter and location table stay hidden until a
  // second one exists.
  { label: 'Locations', render: () => <LocationsPanel /> },
  // Voice agents and the Agent test console are one workflow (configure →
  // test), so they share a single section.
  { label: 'AI Agent', render: () => <AiAgentSection /> },
  // Voice agents (the vendor agents that answer calls) used to be its own tab
  // here. It only matters once a dealership is on the Advanced engine, so it
  // now nests inside VoiceProvidersPanel's Advanced-engine branch instead of
  // requiring a second stop to configure one thing.
  { label: 'Best sellers', render: () => <BestSellersPanel /> },
  { label: 'Integrations', render: () => <IntegrationsPanel /> },
  { label: 'Channels', render: () => <ChannelsPanel /> },
  { label: 'Channel monitor', render: () => <ChannelMonitorFeed /> },
  { label: 'Business hours', render: () => <BusinessHoursPanel /> },
  // Escalation thresholds + the next-step reason list (E10 / E11). Both are
  // per-dealership; without this tab they were SQL-only.
  { label: 'Lead follow-up', render: () => <LeadFollowUpPanel /> },
  { label: 'Roles & teams', render: () => <RolesPanel /> },
  { label: 'Triage rules', render: () => <TriageRulesPanel /> },
  { label: 'Compliance policies', render: () => <CompliancePoliciesPanel /> },
  { label: 'Documents', render: () => <TestDriveDocumentsPanel /> },
];

// Retell voice/behaviour is edited per agent, inside that agent's card under
// Voice agents — it used to be a separate "AI Behaviour" tab with its own
// agent picker.
const AI_AGENT_TABS = [
  { label: 'Voice agents', render: () => <VoiceProvidersPanel /> },
  { label: 'Agent test console', render: () => <AgentTestConsole /> },
];

function AiAgentSection() {
  const [active, setActive] = useState(0);
  return (
    <div className="flex-col" style={{ gap: 16 }}>
      <div className="mode-toggle" role="tablist" aria-label="AI agent" style={{ alignSelf: 'flex-start' }}>
        {AI_AGENT_TABS.map((t, i) => (
          <button
            key={t.label}
            type="button"
            role="tab"
            aria-selected={i === active}
            className={i === active ? 'active' : ''}
            onClick={() => setActive(i)}
          >
            {t.label}
          </button>
        ))}
      </div>
      {AI_AGENT_TABS[active].render()}
    </div>
  );
}

const triageRules = [
  ['Sentiment < −0.4 at any point', 'Flag · review', true], ['Quality score < 50', 'Flag CRITICAL · 2h SLA', true],
  ['Tool-call validator fails', 'Flag HALLUCINATION', true], ['Mandatory disclaimer missed', 'Flag COMPLIANCE', true],
  ['Call duration > 8 min', 'Flag · review', true], ['Customer says "manager", "complaint", "lawyer"', 'Escalate IMMEDIATELY', true],
  ['Repeat caller (same RO)', 'Flag · review', false],
];

/* ─── Tiny preview of each theme ─── */
function ThemePreview({ themeId, mode }) {
  // Mount a sandbox div with theme classes so its CSS vars resolve to that
  // theme's tokens, then paint a mini mock UI with them.
  return (
    <div
      className={`theme-${themeId} mode-${mode} preview`}
      style={{
        background: 'var(--bg-primary)',
        borderColor: 'var(--border-color)',
      }}
    >
      {/* fake sidebar strip */}
      <div style={{
        position: 'absolute', inset: '0 auto 0 0', width: 12,
        background: 'var(--bg-secondary)',
        borderRight: '1px solid var(--border-color)',
      }} />
      {/* fake cards */}
      <div style={{
        position: 'absolute', top: 8, left: 18, right: 8, height: 14,
        background: 'var(--bg-card)',
        border: '1px solid var(--border-color)',
        borderRadius: 'var(--radius-sm)',
      }} />
      <div style={{
        position: 'absolute', top: 26, left: 18, width: 22, height: 18,
        background: 'var(--accent-primary)',
        borderRadius: 'var(--radius-sm)',
      }} />
      <div style={{
        position: 'absolute', top: 26, left: 44, right: 8, height: 18,
        background: 'var(--bg-card)',
        border: '1px solid var(--border-color)',
        borderRadius: 'var(--radius-sm)',
      }} />
    </div>
  );
}

function AppearancePanel() {
  const { theme, mode, setTheme, setMode } = useTheme();

  return (
    <div className="flex-col" style={{ gap: 18 }}>
      <div className="card animate-in">
        <div className="flex-between" style={{ marginBottom: 12 }}>
          <div>
            <div className="card-label" style={{ marginBottom: 4 }}>APPEARANCE</div>
            <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
              Choose how SpectraIQ looks for you. Your selection is saved to this browser.
            </div>
          </div>
          <div className="mode-toggle" role="tablist" aria-label="Color mode">
            <button
              type="button"
              className={mode === 'light' ? 'active' : ''}
              onClick={() => setMode('light')}
              role="tab"
              aria-selected={mode === 'light'}
            >
              <span aria-hidden="true">☀</span> Light
            </button>
            <button
              type="button"
              className={mode === 'dark' ? 'active' : ''}
              onClick={() => setMode('dark')}
              role="tab"
              aria-selected={mode === 'dark'}
            >
              <span aria-hidden="true">☾</span> Dark
            </button>
          </div>
        </div>

        <div className="theme-picker-grid">
          {Object.values(THEMES).map((t) => {
            const selected = t.id === theme;
            const swatches = mode === 'dark' ? t.swatchDark : t.swatchLight;
            const isDefault = t.id === 'office365';
            return (
              <button
                type="button"
                key={t.id}
                className={`theme-card ${selected ? 'selected' : ''}`}
                onClick={() => setTheme(t.id)}
                aria-pressed={selected}
              >
                {selected && <span className="check" aria-hidden="true">✓</span>}
                <ThemePreview themeId={t.id} mode={mode} />
                <div>
                  <div className="flex-between" style={{ marginBottom: 2 }}>
                    <span className="name">{t.label}</span>
                    {isDefault && (
                      <span
                        className="mono text-xs"
                        style={{
                          color: 'var(--text-muted)',
                          fontSize: 9,
                          letterSpacing: 0.5,
                        }}
                      >
                        DEFAULT
                      </span>
                    )}
                  </div>
                  <div className="desc">{t.description}</div>
                </div>
                <div style={{ display: 'flex', gap: 3, marginTop: 2 }}>
                  {swatches.slice(0, 6).map((c, i) => (
                    <span
                      key={i}
                      title={c}
                      style={{
                        width: 14, height: 14,
                        borderRadius: 3,
                        background: c,
                        border: '1px solid rgba(0,0,0,0.08)',
                      }}
                    />
                  ))}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      <div className="card animate-in" style={{ animationDelay: '0.05s' }}>
        <div className="card-label">LIVE PREVIEW</div>
        <div className="grid-3" style={{ gap: 10 }}>
          <div className="card" style={{ padding: 12 }}>
            <div className="card-label">METRIC</div>
            <div className="metric-value" style={{ fontSize: 22, color: 'var(--accent-primary)' }}>1,284</div>
            <div className="metric-sub success">↑ 12.4% this week</div>
          </div>
          <div className="card" style={{ padding: 12 }}>
            <div className="card-label">STATUS PILLS</div>
            <div className="flex-row" style={{ flexWrap: 'wrap', gap: 6 }}>
              <span className="pill success filled">success</span>
              <span className="pill warning filled">warning</span>
              <span className="pill danger filled">danger</span>
              <span className="pill accent filled">accent</span>
            </div>
          </div>
          <div className="card" style={{ padding: 12 }}>
            <div className="card-label">BUTTONS</div>
            <div className="flex-row" style={{ flexWrap: 'wrap', gap: 6 }}>
              <button className="btn primary small">Primary</button>
              <button className="btn small">Secondary</button>
              <button className="btn danger small">Danger</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Organization — dealership-level profile settings. Currently the timezone,
 * which standardises how the whole platform renders wall-clock times (booking
 * diaries, reminders, the AI's sense of "now", ACMA calling windows). Defaults
 * to Australian Eastern time; each dealership can override it here.
 */
function OrganizationPanel() {
  const [org, setOrg] = useState(null);
  const [zones, setZones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [cfg, tz] = await Promise.all([
          organizationService.get().catch(() => ({})),
          organizationService.timezones().catch(() => []),
        ]);
        if (!alive) return;
        setZones(Array.isArray(tz) ? tz : []);
        setOrg({
          name: cfg?.name || '',
          state: cfg?.state || '',
          timezone: cfg?.timezone || 'Australia/Sydney',
        });
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  const set = (k, v) => setOrg((o) => ({ ...o, [k]: v }));

  const save = async () => {
    setSaving(true); setMsg(null);
    try {
      const saved = await organizationService.update({ timezone: org.timezone });
      // Apply immediately so dates across the app re-render in the new zone.
      setOrgTimezone(saved?.timezone || org.timezone);
      setMsg({ ok: true, text: 'Saved — times across SpectraIQ now use this timezone.' });
    } catch (e) {
      setMsg({ ok: false, text: e?.response?.data?.message || e?.message || 'Save failed' });
    } finally {
      setSaving(false);
    }
  };

  if (loading || !org) {
    return (<><PanelHeader title="Organization" sub="Loading…" /></>);
  }

  // Group AU zones separately so the picker leads with the home market.
  const auZones = zones.filter((z) => z.id.startsWith('Australia/'));
  const otherZones = zones.filter((z) => !z.id.startsWith('Australia/'));
  const zoneLabel = (z) => `${z.label}${z.offset ? ` · ${z.offset}` : ''}`;

  return (
    <>
      <PanelHeader
        title="Organization"
        sub="Your dealership's profile. The timezone standardises how every date and time is shown across SpectraIQ."
        action={<button className="btn small primary" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>}
      />

      <div className="card" style={{ display: 'flex', flexDirection: 'column', gap: 16, padding: 16 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 14 }}>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span className="card-label">Dealership</span>
            <input className="input" value={org.name} disabled readOnly />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span className="card-label">State</span>
            <input className="input" value={org.state || '—'} disabled readOnly />
          </label>
        </div>

        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, maxWidth: 420 }}>
          <span className="card-label">Timezone</span>
          <select
            className="btn small"
            value={org.timezone}
            onChange={(e) => set('timezone', e.target.value)}
          >
            {auZones.length > 0 && (
              <optgroup label="Australia">
                {auZones.map((z) => <option key={z.id} value={z.id}>{zoneLabel(z)}</option>)}
              </optgroup>
            )}
            {otherZones.length > 0 && (
              <optgroup label="Other">
                {otherZones.map((z) => <option key={z.id} value={z.id}>{zoneLabel(z)}</option>)}
              </optgroup>
            )}
            {zones.length === 0 && <option value={org.timezone}>{org.timezone}</option>}
          </select>
          <span className="mono text-xs text-muted" style={{ lineHeight: 1.6 }}>
            Default is Australian Eastern time. This drives booking diaries, appointment
            reminders, the AI assistant's sense of “now”, and ACMA calling windows.
            Timestamps are always stored in UTC and shown to each dealership in its own clock.
          </span>
        </label>

        {msg && (
          <div style={{ fontSize: 13, color: msg.ok ? 'var(--success)' : 'var(--danger)' }}>{msg.text}</div>
        )}
      </div>
    </>
  );
}

export default function SettingsPage() {
  const [activeNav, setActiveNav] = useState(0);

  return (
    <>
      <TopBar crumbs={['Settings']} />
      <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        <div className="settings-subnav" style={{
          width: 200, padding: '16px 12px',
          borderRight: '1px solid var(--border-color)',
          display: 'flex', flexDirection: 'column', gap: 2, overflow: 'auto',
          background: 'var(--bg-secondary)',
        }}>
          {SETTINGS_TABS.map((tab, i) => (
            <div
              key={tab.label}
              className={`settings-subnav-item ${i === activeNav ? 'active' : ''}`}
              onClick={() => setActiveNav(i)}
              style={{
                padding: '9px 12px',
                borderRadius: 'var(--radius-sm)',
                fontSize: 13,
                fontWeight: 500,
                cursor: 'pointer',
                transition: 'all var(--transition-fast)',
              }}
            >
              {tab.label}
            </div>
          ))}
        </div>

        <div style={{ flex: 1, overflow: 'auto', padding: 20 }}>
          {(SETTINGS_TABS[activeNav] || SETTINGS_TABS[0]).render()}
        </div>
      </div>

      {/* The WhatsApp QR modal now belongs to ChannelsPanel, which is the only
          thing that opens it. */}
    </>
  );
}

/* ────────────────────────────────────────────────────────────
   Per-nav-item panels
   ──────────────────────────────────────────────────────────── */

function PanelHeader({ title, sub, action }) {
  return (
    <div className="flex-between" style={{ marginBottom: 14 }}>
      <div>
        <div style={{ fontSize: 16, fontWeight: 600 }}>{title}</div>
        {sub && <div className="mono text-xs text-muted" style={{ marginTop: 4 }}>{sub}</div>}
      </div>
      {action}
    </div>
  );
}

/* Small status pill helper for the Integrations panel. Keeps the
   connected/needs-setup wording (and colour) in one place. */
function IntegrationRow({ name, desc, status, tone, action }) {
  return (
    <div className="flex-row" style={{ padding: '11px 0', borderBottom: '1px solid var(--border-subtle)', alignItems: 'flex-start' }}>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 13, fontWeight: 500 }}>{name}</div>
        {desc && <div className="mono text-xs text-muted" style={{ marginTop: 2 }}>{desc}</div>}
      </div>
      <span className={`mono text-xs text-${tone || 'muted'}`} style={{ whiteSpace: 'nowrap', marginTop: 1 }}>{status}</span>
      {action}
    </div>
  );
}

/**
 * Integrations — the single, merged view of everything SpectraIQ talks to
 * (formerly split across "Integrations · Dealer system" and "Marketplaces").
 *
 * Design rule for this panel: show ACTUAL state, never fabricated metrics.
 *   • Communication channels + voice are read live from the backend
 *     (/v1/channels/status, /v1/voice-providers).
 *   • The dealer management system is genuinely per-tenant config, so it's
 *     described as a capability with a link to where it's set up — no invented
 *     "connected 2m ago".
 *   • Inbound lead sources list the source *types* the platform recognises
 *     (real values from the lead pipeline), without made-up per-marketplace
 *     lead counts or connection badges we don't actually track.
 */
function IntegrationsPanel() {
  const [ch, setCh] = useState(undefined);      // undefined = loading
  const [voice, setVoice] = useState(undefined);
  const [err, setErr] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      const [c, v] = await Promise.all([
        channelsService.status().catch(() => null),
        voiceProvidersService.get().catch(() => null),
      ]);
      if (!alive) return;
      if (c == null && v == null) setErr(true);
      setCh(c || null);
      setVoice(v || null);
    })();
    return () => { alive = false; };
  }, []);

  const loading = ch === undefined || voice === undefined;

  // ── Derive real channel status ──
  const twilio = ch?.twilio || { configured: false, status: 'NOT_CONFIGURED' };
  const wa = ch?.whatsapp || { connected: false };
  const twilioConnected = twilio.status === 'ACTIVE' || (twilio.configured && twilio.status !== 'INVALID');
  const voiceConfigured = !!(voice && (voice.hasCredentials || voice.stt || voice.tts));

  return (
    <div className="flex-col" style={{ gap: 18 }}>
      <div className="card animate-in">
        <PanelHeader
          title="Integrations"
          sub="Everything SpectraIQ connects to — communication channels, voice AI, your dealer system and inbound lead sources."
        />

        {err && (
          <div className="mono text-xs" style={{ color: 'var(--warning)', marginBottom: 8 }}>
            Couldn’t reach the backend — live connection status is unavailable right now.
          </div>
        )}

        {loading ? (
          <div className="mono text-xs text-muted" style={{ padding: '8px 0' }}>Checking connections…</div>
        ) : (
          <>
            <IntegrationRow
              name="Telephony & SMS"
              desc="Voice calls and text messaging for the AI assistant — set up under the Channels tab."
              status={twilioConnected ? 'Connected' : 'Not configured'}
              tone={twilioConnected ? 'success' : 'muted'}
            />
            <IntegrationRow
              name="WhatsApp"
              desc="Two-way WhatsApp — QR pairing or a Twilio sender."
              status={wa.connected ? 'Connected' : 'Not connected'}
              tone={wa.connected ? 'success' : 'muted'}
            />
            <IntegrationRow
              name="Voice AI (speech-to-text & text-to-speech)"
              desc="The providers that let the assistant hear and speak."
              status={voiceConfigured ? 'Configured' : 'Not configured'}
              tone={voiceConfigured ? 'success' : 'muted'}
            />
            <IntegrationRow
              name="Dealer management system"
              desc="Stock, customers and bookings sync with your system of record. Set up per dealership by the SpectraIQ team."
              status="Per dealership"
            />
          </>
        )}
      </div>

      {/* Inbound lead sources — real source types, no fabricated stats. */}
      <div className="card animate-in" style={{ animationDelay: '0.05s' }}>
        <PanelHeader
          title="Inbound lead sources"
          sub="Where new leads come from. SpectraIQ ingests and classifies leads from these sources automatically — no per-source connection to manage here."
        />
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {LEAD_SOURCE_TYPES.map((s) => (
            <span key={s} className="pill accent" style={{ fontSize: 12 }}>{s}</span>
          ))}
        </div>
        <div className="mono text-xs text-muted" style={{ marginTop: 12, lineHeight: 1.6 }}>
          Web-form, phone and messaging leads flow in through your connected channels above.
          Marketplace feeds (e.g. Carsales, Drive) are provisioned per dealership.
        </div>
      </div>
    </div>
  );
}

/* The inbound source *types* the pipeline recognises — mirrors the labels in
   lib/leadSource.js. These are capabilities, not live connections, so there
   are deliberately no counts or status badges attached. */
const LEAD_SOURCE_TYPES = [
  'Carsales', 'Drive', 'CarExpert', 'Autotrader', 'Facebook',
  'Web form', 'Phone', 'Walk-in', 'WhatsApp', 'SMS', 'Email',
];

/*
 * The Channels panel used to live here: a static list with one live WhatsApp
 * row. It's now a real, backend-backed panel (Twilio credentials, per-number
 * routing, both WhatsApp providers, call activity) and has moved to
 * components/settings/ChannelsPanel.jsx, which owns its own polling and the QR
 * modal. Imported at the top of this file.
 */

function BusinessHoursPanel() {
  const days = [
    ['Mon', '08:00', '18:00', true],
    ['Tue', '08:00', '18:00', true],
    ['Wed', '08:00', '18:00', true],
    ['Thu', '08:00', '18:00', true],
    ['Fri', '08:00', '18:00', true],
    ['Sat', '09:00', '16:00', true],
    ['Sun', '—', '—', false],
  ];
  return (
    <div className="card animate-in">
      <PanelHeader
        title="Business hours"
        sub={`ACMA-compliant outbound calling windows (${orgTimezoneAbbrev()}) — outbound voice + SMS are blocked outside these times. Set your timezone under Organization.`}
      />
      {days.map(([d, open, close, on], i) => (
        <div key={i} className="flex-row" style={{ padding: '8px 0', borderBottom: '1px solid var(--border-subtle)', fontSize: 13 }}>
          <span style={{ width: 48, fontWeight: 500 }}>{d}</span>
          <span className="mono text-xs text-muted">{open} → {close}</span>
          <div style={{ marginLeft: 'auto', width: 32, height: 18, borderRadius: 9, background: on ? 'var(--accent-primary)' : 'var(--border-color)', position: 'relative', cursor: 'pointer' }}>
            <div style={{ position: 'absolute', top: 2, left: on ? 16 : 2, width: 14, height: 14, borderRadius: '50%', background: '#fff', transition: 'left 0.2s' }} />
          </div>
        </div>
      ))}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 14 }}>
        <button className="btn small">Reset</button>
        <button className="btn small primary">Save changes</button>
      </div>
    </div>
  );
}

function RolesPanel() {
  const [users, setUsers] = useState(null);   // null = loading
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const me = await usersService.me();
        if (me?.dealershipId == null) { if (!cancelled) setUsers([]); return; }
        const rows = await usersService.listByDealership(me.dealershipId, { page: 0, size: 200 });
        if (!cancelled) setUsers(Array.isArray(rows) ? rows : []);
      } catch (_) {
        if (!cancelled) { setError(true); setUsers([]); }
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Count real team members per role.
  const countFor = (role) => (users || []).filter(u => (u.roles || []).includes(role)).length;

  return (
    <div className="card animate-in">
      <PanelHeader
        title="Roles & teams"
        sub="Invite consultants and managers, assign roles, activate or deactivate accounts."
        action={<Link to="/team" className="btn small primary">Manage team →</Link>}
      />

      {error && (
        <div className="mono text-xs" style={{ color: 'var(--warning)', marginBottom: 8 }}>
          Couldn’t load the live team — showing the role reference only.
        </div>
      )}

      {ASSIGNABLE_ROLES.map((role) => {
        const meta = ROLE_META[role];
        const n = countFor(role);
        return (
          <div key={role} style={{ padding: '10px 0', borderBottom: '1px solid var(--border-subtle)' }}>
            <div className="flex-between">
              <span style={{ fontSize: 13, fontWeight: 500, display: 'flex', alignItems: 'center', gap: 8 }}>
                <span className={`pill ${roleTone(role)} filled`}>{roleLabel(role)}</span>
              </span>
              <span className="mono text-xs text-muted">
                {users == null ? '…' : `${n} ${n === 1 ? 'person' : 'people'}`}
              </span>
            </div>
            <div className="mono text-xs text-muted" style={{ marginTop: 3 }}>{meta.desc}</div>
          </div>
        );
      })}

      <div className="mono text-xs text-muted" style={{ marginTop: 12, lineHeight: 1.6 }}>
        Roles map to Keycloak realm roles. Invited users confirm via email before they can sign in.
      </div>
    </div>
  );
}

function TriageRulesPanel() {
  return (
    <div className="card animate-in">
      <PanelHeader
        title="Triage auto-flag rules"
        sub="When the rules below trigger, the call is flagged for human review."
        action={<button className="btn small primary">+ Add rule</button>}
      />
      {triageRules.map(([cond, act, on], i) => (
        <div key={i} className="flex-row" style={{ padding: '10px 0', borderBottom: '1px solid var(--border-subtle)' }}>
          <div style={{ width: 32, height: 18, borderRadius: 9, background: on ? 'var(--accent-primary)' : 'var(--border-color)', position: 'relative', flexShrink: 0, cursor: 'pointer' }}>
            <div style={{ position: 'absolute', top: 2, left: on ? 16 : 2, width: 14, height: 14, borderRadius: '50%', background: '#fff', transition: 'left 0.2s' }} />
          </div>
          <span style={{ flex: 1, fontSize: 13 }}>{cond}</span>
          <span className="mono text-xs text-muted">→ {act}</span>
        </div>
      ))}
    </div>
  );
}

function CompliancePoliciesPanel() {
  const policies = [
    ['ACMA DNC honoured', 'Strict · 30-day cache', true],
    ['Spam Act consent required', 'Block sends without proof', true],
    ['PII redaction on transcripts', 'Phone, email, address, RO', true],
    ['Mandatory AI disclaimer', 'Prepend to every outbound greeting', true],
    ['Data residency', 'ap-southeast-2 (Sydney)', true],
    ['Audit log retention', '7 years', true],
  ];
  return (
    <div className="card animate-in">
      <PanelHeader title="Compliance policies" sub="Hard-wired guardrails enforced at the gateway, not by the agent prompt." />
      {policies.map(([k, v, on], i) => (
        <div key={i} className="flex-row" style={{ padding: '10px 0', borderBottom: '1px solid var(--border-subtle)' }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13, fontWeight: 500 }}>{k}</div>
            <div className="mono text-xs text-muted" style={{ marginTop: 2 }}>{v}</div>
          </div>
          <span className={`pill ${on ? 'success' : 'danger'} filled`}>{on ? 'ENFORCED' : 'OFF'}</span>
        </div>
      ))}
    </div>
  );
}

